import { useLang } from 'hooks/lang';

import { actions, calculateProjectTotal } from 'entities';

import { Modal, LabeledTextarea, Button, Column, Row, LabeledSelect } from 'components/Blocks';
import { EntitySelect } from 'components/Entities';
import { CirclePlusIcon, CircleXIcon, SendIcon } from 'components/Icons';

import { ProjectInvoiceValue } from '../ProjectInvoiceValue';
import { InlineAlert } from '../InlineAlert';

import { useFormControl, useToggleV2 } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { calculateAvailableAmount } from '../utils';
import { INVOICE_COUNTRIES } from 'utils/countries';
import { CURRENCY_OPTIONS } from 'utils/constants';

export function CreateQuotationRequest() {
  const { t } = useLang();

  const [on, onHandlers] = useToggleV2();

  const { project, updateProject } = useProjectProfile();

  const isPackage = project?.invoice.type === 'PACKAGE';

  const { state, dispatch, handleChange, resetDefault } = useFormControl({
    country: '',
    client: project.client,
    comment: '',
    value: '',
    currency: '',
  });

  const canCreate = [
    Object.values(state).every(Boolean),
    !!state.value,
    isPackage
      ? Number(state.value) === Number(calculateProjectTotal(project.services))
      : Number(state.value) <= calculateAvailableAmount(project),
  ].every(Boolean);

  const close = (): void => {
    onHandlers.turnOff();
    resetDefault();
  };

  const open = (): void => {
    if (isPackage) dispatch({ value: calculateProjectTotal(project.services) });

    onHandlers.turnOn();
  };

  const createQuotation = actions.invoice.createQuotationRequest.use({
    block: !canCreate,

    params: {
      project: project.id,
      ...state,
    },

    onSuccess: (request) => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          quotationRequests: [...old.invoice.quotationRequests, request],
        },
      }));

      close();
    },
  });

  return (
    <>
      <Modal
        close={close}
        isOpen={on}
        title={t('create-quotation-request') as string}
        width="min(90vw, 770px)"
        allowEscape={false}
      >
        <div className="grid grid-cols-2 gap-4 small_tablet:grid-cols-1">
          <LabeledSelect
            id="country"
            options={INVOICE_COUNTRIES}
            value={state.country}
            onChange={handleChange}
            label={t('country')}
          />

          <EntitySelect
            target="clients.list"
            labelKey="name"
            value={state.client}
            onChange={handleChange}
            id="client"
            label={t('client')}
          />

          <LabeledSelect
            onChange={handleChange}
            label={t('currency-to-quote')}
            value={state.currency}
            id={'currency'}
            options={CURRENCY_OPTIONS.filter((c) => c.value !== project.currency)}
          />

          <ProjectInvoiceValue
            state={state}
            dispatch={dispatch as () => void}
            disabled={isPackage}
          />
        </div>

        <LabeledTextarea
          label="Comment"
          value={state.comment}
          placeholder={t('type-here-a-comment') as string}
          id="comment"
          onChange={handleChange}
        />

        <Column gap="6">
          <InlineAlert
            title={t('attention')}
            description={t(
              'please-provide-details-as-to-why-its-necessary-to-invoice-in-a-different-currency-reference-services-affected-countries-etc',
            )}
          />

          <Row justifyContent="space-between">
            <Button variant="outlined" onClick={close}>
              <CircleXIcon />
              {t('cancel')}
            </Button>

            <Button disabled={!canCreate} {...createQuotation.buttonProps}>
              <SendIcon />
              {t('create-request')}
            </Button>
          </Row>
        </Column>
      </Modal>

      <Button onClick={open} variant="ghost">
        <CirclePlusIcon />
        <span className="phone:hidden">{t('create')}</span>
      </Button>
    </>
  );
}
